'use client';

import { c as _c } from "react/compiler-runtime";
import { FC, useRef, useState } from 'react';
import Image from '@/components/Image';
import Link from '@/components/Link';
import AnimatedLink from '@/components/shared/AnimatedLink';
import DialogCloseCustomIcon from '@/components/shared/Dialog/DialogCloseCustomIcon';
import EmailButton from '@/components/shared/Footer/EmailButton';
import { Typography } from '@/components/shared/Typography';
import cn from '@/helpers/cn';
import useDevice from '@/hooks/useDevice';
import { useModal } from '@/hooks/useModal';
import CaseStudyCard from './CaseStudyCard';
import css from './style.module.scss';
import { ModalMenuData } from './types';
interface Props {
  data: ModalMenuData;
}
const MenuNavigationModal: FC<Props> = t0 => {
  const $ = _c(46);
  const {
    data
  } = t0;
  const cardRef = useRef(null);
  const {
    closeModal
  } = useModal();
  const device = useDevice();
  const [bgLoaded, setBgLoaded] = useState(false);
  const bgSrc = device === "mobile" ? "/assets/images/menu/menu-texture-mob.webp" : "/assets/images/menu/menu-texture.webp";
  let t1;
  if ($[0] !== closeModal) {
    t1 = () => {
      requestAnimationFrame(() => {
        closeModal();
      });
    };
    $[0] = closeModal;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleClose = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DialogCloseCustomIcon variant="tapeGrayDark" className={css.close} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = bgLoaded ? css.isReady : "";
  let t4;
  if ($[3] !== t3) {
    t4 = cn(css.modalWrapper, t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== bgSrc || $[6] !== device) {
    t5 = device === "desktop" ? <div className={css.bgLayer} aria-hidden="true"><Image fill={true} preload={true} src={bgSrc} alt="" loading="eager" sizes="100vw" className={css.bgCover} onLoad={() => setBgLoaded(true)} /></div> : null;
    $[5] = bgSrc;
    $[6] = device;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== data.menuItems || $[9] !== handleClose) {
    t6 = data.menuItems?.map(item => {
      const isLongText = item?.text?.toLocaleLowerCase().includes("production");
      return <Link key={item.id ?? item?.text} href={item.url} target={item.target || "_self"} className={cn("js-menu-nav-link", css.navLink, isLongText ? css.longText : "")} onClick={handleClose}>{item.text}</Link>;
    });
    $[8] = data.menuItems;
    $[9] = handleClose;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <nav className={css.navigation} aria-label="Main navigation">{t6}</nav>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== data.location || $[14] !== data.socials || $[15] !== device || $[16] !== handleClose) {
    t8 = device === "desktop" ? <div className={css.bottomLeft}><ul className={css.socials}>{data.socials?.map((t9, index) => {
          const {
            id,
            name,
            url
          } = t9;
          return <li key={`${id}-${index}`}><AnimatedLink href={url} target="_blank" rel="noreferrer" className={`${css.socialLink} js-menu-footer-item`} onClick={handleClose}>{name}</AnimatedLink></li>;
        })}</ul>{data.location ? <Typography className={`${css.metaText} js-menu-footer-item`} variant="body-m" tag="div">{data.location}</Typography> : null}</div> : null;
    $[13] = data.location;
    $[14] = data.socials;
    $[15] = device;
    $[16] = handleClose;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <div className={css.left}>{t7}{t8}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== data.caseStudies || $[22] !== handleClose) {
    t10 = data.caseStudies?.map(item_0 => <CaseStudyCard key={item_0.id ?? item_0.title} data={item_0} className="js-menu-case" onClick={handleClose} />);
    $[21] = data.caseStudies;
    $[22] = handleClose;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10) {
    t11 = <div className={css.caseStudies}>{t10}</div>;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== data.collabText || $[27] !== data.email || $[28] !== device) {
    t12 = device === "desktop" ? <div className={css.bottomRight}>{data.collabText ? <Typography className={`${css.metaText} js-menu-footer-item`} variant="body-m" tag="p">{data.collabText}</Typography> : null}{data.email ? <EmailButton text={data.email} size="small" className={`${css.emailCopy} js-menu-footer-item`} /> : null}</div> : null;
    $[26] = data.collabText;
    $[27] = data.email;
    $[28] = device;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t11 || $[31] !== t12) {
    t13 = <div className={css.right}>{t11}{t12}</div>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== data.email || $[34] !== data.location || $[35] !== data.socials || $[36] !== device) {
    t14 = device === "mobile" ? <div className={css.mobileFooter}><ul className={css.socials}>{data.socials?.map(_temp)}</ul><div className={css.bottomRight}>{data.location ? <Typography className={`${css.metaText} js-menu-footer-item`} variant="body-m" tag="div">{data.location}</Typography> : null}{data.email ? <EmailButton text={data.email} size="small" className={`${css.emailCopy} js-menu-footer-item`} /> : null}</div></div> : null;
    $[33] = data.email;
    $[34] = data.location;
    $[35] = data.socials;
    $[36] = device;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t13 || $[39] !== t14 || $[40] !== t9) {
    t15 = <div className={css.modalInner}>{t9}{t13}{t14}</div>;
    $[38] = t13;
    $[39] = t14;
    $[40] = t9;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t15 || $[43] !== t4 || $[44] !== t5) {
    t16 = <>{t2}<div ref={cardRef} className={t4}>{t5}{t15}</div></>;
    $[42] = t15;
    $[43] = t4;
    $[44] = t5;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
};
export default MenuNavigationModal;
function _temp(t0, index_0) {
  const {
    id: id_0,
    name: name_0,
    url: url_0
  } = t0;
  return <li key={`${id_0}-${index_0}`}><a href={url_0} target="_blank" rel="noreferrer"><Image src={`/assets/icons/socials/${name_0}.svg`} width="28" height="28" alt={name_0} /></a></li>;
}